import { useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { CONTACT } from "@/lib/products";
import { trackWhatsAppClick } from "@/lib/analytics";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Gift, Loader2, Building2, Calendar, Package } from "lucide-react";

const PRODUCT_OPTIONS = [
  "Engraved Coins",
  "Lockets & Pendants",
  "Silver Plate Momentos",
  "Charm Bracelets",
  "Mixed Assortment",
];

const OCCASIONS = [
  "Corporate gifting",
  "Wedding trousseau",
  "Anniversary",
  "Retirement",
  "Awards ceremony",
  "Festival hamper",
  "Other",
];

export default function BulkGiftingSection() {
  const [form, setForm] = useState({
    company: "",
    customer_name: "",
    customer_phone: "",
    customer_email: "",
    product_interest: PRODUCT_OPTIONS[0],
    quantity: 10,
    delivery_window: "",
    occasion: OCCASIONS[0],
    event_date: "",
    notes: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const submit = async () => {
    if (!form.customer_name.trim() || !form.customer_phone.trim() || !form.delivery_window.trim()) {
      toast.error("Name, phone and delivery window are required");
      return;
    }
    const qty = Number(form.quantity);
    if (!qty || qty < 10) {
      toast.error("Bulk orders start at 10 pieces");
      return;
    }
    setSubmitting(true);
    try {
      const bulk = { ...form, quantity: qty, id: `SOC-BULK-${Date.now().toString(36).toUpperCase()}` };

      const msg = [
        `*Bulk Gifting Enquiry*`,
        "",
        `Company: ${bulk.company || "—"}`,
        `Name: ${bulk.customer_name}`,
        `Phone: ${bulk.customer_phone}`,
        `Quantity: ${bulk.quantity}`,
        `Product interest: ${bulk.product_interest}`,
        `Delivery window: ${bulk.delivery_window}`,
        `Occasion: ${bulk.occasion || "—"}`,
        bulk.notes ? `Notes: ${bulk.notes}` : null,
        "",
        `Enquiry ID: ${bulk.id}`,
      ]
        .filter(Boolean)
        .join("\n");
      const waUrl = `${CONTACT.whatsapp}?text=${encodeURIComponent(msg)}`;

      trackWhatsAppClick({ placement: "bulk_gifting_form" });
      toast.success("Opening WhatsApp to send your enquiry…");
      window.open(waUrl, "_blank");

      setForm((s) => ({ ...s, notes: "", customer_email: "" }));
    } catch (e) {
      toast.error("Something went wrong. Please WhatsApp us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="bulk" className="relative py-24 md:py-32 border-t border-white/5">
      <div className="max-w-7xl mx-auto px-5 md:px-10">
        <div className="grid lg:grid-cols-12 gap-12">
          {/* Left copy */}
          <div className="lg:col-span-5">
            <div className="text-xs tracking-widest-2 uppercase text-[#d4af37]">Section 02 · Corporate & Bulk</div>
            <h2 className="mt-4 font-display text-4xl md:text-6xl leading-[1.05] text-white">
              Gift, <span className="italic gold-gradient-text">at scale.</span>
            </h2>
            <p className="mt-5 text-white/70 md:text-lg leading-relaxed">
              Weddings, retirements, awards nights, festival hampers — from ten pieces to a thousand,
              we curate a signature keepsake for your entire guest list.
            </p>

            <div className="mt-10 space-y-5">
              <Perk icon={<Package size={18} />} title="From 10 pieces" body="Silver, gold, mixed metal — mix product families in a single order." />
              <Perk icon={<Building2 size={18} />} title="Company logo etching" body="Add your emblem to every coin, bracelet or plate at no extra design fee." />
              <Perk icon={<Calendar size={18} />} title="Rush windows" body="Tell us your delivery deadline — we'll confirm feasibility within 24 hours." />
            </div>
          </div>

          {/* Right form */}
          <div className="lg:col-span-7">
            <div className="relative">
              <div aria-hidden className="absolute -inset-3 gold-glow rounded-3xl blur-xl opacity-40" />
              <div className="relative glass-strong rounded-3xl p-6 md:p-10">
                <div className="flex items-center gap-3">
                  <span className="w-11 h-11 rounded-full bg-gradient-to-br from-[#f3e5ab] via-[#d4af37] to-[#7a5d10] text-black flex items-center justify-center">
                    <Gift size={20} />
                  </span>
                  <div>
                    <div className="font-display text-2xl text-white">Request a bulk quote</div>
                    <div className="text-xs text-white/50 uppercase tracking-widest-2 mt-1">10+ pieces · 24-hour reply</div>
                  </div>
                </div>

                <div className="mt-8 grid md:grid-cols-2 gap-5">
                  <Field label="Company / Organisation">
                    <Input
                      value={form.company}
                      onChange={(e) => set("company", e.target.value)}
                      className="bg-white/5 border-white/10 focus:border-[#d4af37] text-white"
                      placeholder="Optional"
                      data-testid="bulk-company"
                    />
                  </Field>
                  <Field label="Your name *">
                    <Input
                      value={form.customer_name}
                      onChange={(e) => set("customer_name", e.target.value)}
                      className="bg-white/5 border-white/10 focus:border-[#d4af37] text-white"
                      placeholder="Full name"
                      data-testid="bulk-name"
                    />
                  </Field>
                  <Field label="Phone (WhatsApp) *">
                    <Input
                      value={form.customer_phone}
                      onChange={(e) => set("customer_phone", e.target.value)}
                      className="bg-white/5 border-white/10 focus:border-[#d4af37] text-white"
                      placeholder="+91"
                      inputMode="tel"
                      data-testid="bulk-phone"
                    />
                  </Field>
                  <Field label="Email">
                    <Input
                      value={form.customer_email}
                      onChange={(e) => set("customer_email", e.target.value)}
                      className="bg-white/5 border-white/10 focus:border-[#d4af37] text-white"
                      placeholder="you@company.com"
                      inputMode="email"
                      data-testid="bulk-email"
                    />
                  </Field>
                  <Field label="Product interest">
                    <SelectBox
                      value={form.product_interest}
                      onChange={(v) => set("product_interest", v)}
                      options={PRODUCT_OPTIONS}
                      testid="bulk-product-interest"
                    />
                  </Field>
                  <Field label="Quantity *">
                    <Input
                      type="number"
                      min={10}
                      value={form.quantity}
                      onChange={(e) => set("quantity", e.target.value)}
                      className="bg-white/5 border-white/10 focus:border-[#d4af37] text-white"
                      data-testid="bulk-quantity"
                    />
                  </Field>
                  <Field label="Delivery window *">
                    <Input
                      value={form.delivery_window}
                      onChange={(e) => set("delivery_window", e.target.value)}
                      className="bg-white/5 border-white/10 focus:border-[#d4af37] text-white"
                      placeholder="e.g. Within 3 weeks, or By 20 Sept"
                      data-testid="bulk-delivery-window"
                    />
                  </Field>
                  <Field label="Occasion">
                    <SelectBox
                      value={form.occasion}
                      onChange={(v) => set("occasion", v)}
                      options={OCCASIONS}
                      testid="bulk-occasion"
                    />
                  </Field>
                  <Field label="Event date">
                    <Input
                      type="date"
                      value={form.event_date}
                      onChange={(e) => set("event_date", e.target.value)}
                      className="bg-white/5 border-white/10 focus:border-[#d4af37] text-white [color-scheme:dark]"
                      data-testid="bulk-event-date"
                    />
                  </Field>
                  <div className="md:col-span-2">
                    <Field label="Notes for our team">
                      <Textarea
                        value={form.notes}
                        onChange={(e) => set("notes", e.target.value)}
                        rows={3}
                        className="bg-white/5 border-white/10 focus:border-[#d4af37] text-white"
                        placeholder="Colour preferences, logo files, budget indication…"
                        data-testid="bulk-notes"
                      />
                    </Field>
                  </div>
                </div>

                <div className="mt-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                  <p className="text-xs text-white/50 max-w-sm">
                    We reply on WhatsApp within one business day with a per-piece quote and design timeline.
                  </p>
                  <Button
                    onClick={submit}
                    disabled={submitting}
                    className="rounded-full bg-[#d4af37] text-black hover:brightness-110 min-w-[200px] h-12"
                    data-testid="bulk-submit"
                  >
                    {submitting ? (
                      <span className="flex items-center gap-2"><Loader2 className="animate-spin" size={16} /> Submitting…</span>
                    ) : (
                      "Request Quote"
                    )}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

const Perk = ({ icon, title, body }) => (
  <div className="flex gap-4 items-start">
    <span className="w-10 h-10 rounded-full glass flex items-center justify-center text-[#d4af37] shrink-0">{icon}</span>
    <div>
      <div className="font-display text-lg text-white">{title}</div>
      <div className="text-sm text-white/60 mt-1 leading-relaxed">{body}</div>
    </div>
  </div>
);

const Field = ({ label, children }) => (
  <label className="block">
    <span className="block text-[11px] uppercase tracking-widest-2 text-white/55 mb-2">{label}</span>
    {children}
  </label>
);

const SelectBox = ({ value, onChange, options, testid }) => (
  <select
    value={value}
    onChange={(e) => onChange(e.target.value)}
    className="w-full h-10 rounded-md bg-white/5 border border-white/10 focus:border-[#d4af37] focus:outline-none text-white px-3 text-sm"
    data-testid={testid}
  >
    {options.map((o) => (
      <option key={o} value={o} className="bg-black">{o}</option>
    ))}
  </select>
);
